/**
 * Settings → Integrations landing page.
 *
 * - No integration exists for the active tenant → render the Connect CTA card,
 *   which opens ConnectSubContractorHubPanel (the paste-and-test form).
 * - Integration row exists → render SyncStatusCard. Its "Configure" button
 *   opens the 5-tab SubContractorHubPanel (Story SCH-2.1). Its
 *   "Reconnect to a different account" button opens ReconnectPanel directly.
 *
 * Story SCH-2.1 rebound `onConfigure` from the SCH-1.3 temporary RotateKeyPanel
 * home to the new 5-tab Configure panel. RotateKeyPanel and ReconnectPanel are
 * still reachable as standalone slide-outs — the Configure panel's Connection
 * tab closes itself and opens the appropriate one when the user clicks the
 * Rotate or Reconnect CTA inside.
 */

import { useCallback, useState } from 'react';
import { Loader2, Plug } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import {
  useDisableSubContractorHubIntegration,
  useEnableSubContractorHubIntegration,
  useResumeAfterDrop,
  useResumeAfterFailures,
  useSubContractorHubIntegration,
} from '@/hooks/integrations/useSubContractorHubConnection';
import {
  useCanRunBackfill,
  useSyncNow,
} from '@/hooks/integrations/useBackfillProgress';
import { ConnectSubContractorHubPanel } from './subcontractorhub/ConnectSubContractorHubPanel';
import { SubContractorHubPanel } from './subcontractorhub/SubContractorHubPanel';
import { SyncStatusCard } from './subcontractorhub/SyncStatusCard';
import { RotateKeyPanel } from './subcontractorhub/RotateKeyPanel';
import { DeleteIntegrationDialog } from './subcontractorhub/DeleteIntegrationDialog';
import { ReconnectPanel } from './subcontractorhub/ReconnectPanel';

export function IntegrationsTab() {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isConfigureOpen, setIsConfigureOpen] = useState(false);
  const [isRotateOpen, setIsRotateOpen] = useState(false);
  const [isReconnectOpen, setIsReconnectOpen] = useState(false);
  const [isDisableConfirmOpen, setIsDisableConfirmOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { data: integration, isLoading, error } = useSubContractorHubIntegration();
  const { toast } = useToast();
  const disableMutation = useDisableSubContractorHubIntegration();
  const enableMutation = useEnableSubContractorHubIntegration();
  const syncNowMutation = useSyncNow();
  const resumeAfterDropMutation = useResumeAfterDrop();
  const resumeAfterFailuresMutation = useResumeAfterFailures();
  const { canRun: canSyncNow } = useCanRunBackfill();
  const [isResumeConfirmOpen, setIsResumeConfirmOpen] = useState(false);
  const [resumeConfirmText, setResumeConfirmText] = useState('');

  // The Fly.io machines auto-suspend after idle periods, so the first
  // request after wake-up can take several seconds. We keep the dialog
  // open with a spinner until the mutation resolves so the user gets
  // clear feedback rather than a frozen-looking UI.
  const handleConfirmDisable = useCallback(() => {
    disableMutation.mutate(undefined, {
      onSuccess: (response) => {
        if (response.error) {
          toast({
            title: 'Could not disable integration',
            description: 'Please try again.',
            variant: 'destructive',
          });
        } else {
          toast({ title: 'Integration disabled' });
        }
        setIsDisableConfirmOpen(false);
      },
      onError: () => {
        toast({
          title: 'Could not disable integration',
          description: 'Please try again.',
          variant: 'destructive',
        });
        setIsDisableConfirmOpen(false);
      },
    });
  }, [disableMutation, toast]);

  const handleEnable = useCallback(() => {
    enableMutation.mutate(undefined, {
      onSuccess: (response) => {
        if (response.error) {
          toast({
            title: 'Could not enable integration',
            description: 'Please try again.',
            variant: 'destructive',
          });
          return;
        }
        toast({ title: 'Integration enabled' });
      },
      onError: () => {
        toast({
          title: 'Could not enable integration',
          description: 'Please try again.',
          variant: 'destructive',
        });
      },
    });
  }, [enableMutation, toast]);

  // SCH-5.5: resume after project-count drop. Typed-confirm guard before the
  // mutation fires so admin must affirmatively type "Resume" to proceed.
  const handleResumeConfirm = useCallback(() => {
    if (resumeConfirmText.trim() !== 'Resume') return;
    resumeAfterDropMutation.mutate(undefined, {
      onSuccess: (response) => {
        if (response.error) {
          toast({
            title: 'Could not resume sync',
            description: response.error.message,
            variant: 'destructive',
          });
          return;
        }
        toast({
          title: 'Sync resumed',
          description:
            response.data.new_baseline !== null
              ? `Baseline reset to ${response.data.new_baseline} projects.`
              : 'Baseline left unchanged (SubContractorHub unreachable during resume).',
        });
        setIsResumeConfirmOpen(false);
        setResumeConfirmText('');
      },
      onError: () =>
        toast({
          title: 'Could not resume sync',
          description: 'Please try again.',
          variant: 'destructive',
        }),
    });
  }, [resumeAfterDropMutation, resumeConfirmText, toast]);

  // SCH-5.7: resume a sync auto-paused after consecutive failures. Lower-risk
  // than the count-drop resume (no re-baseline / data-loss implication), so it
  // fires directly on click rather than behind a typed-confirm.
  const handleResumeAfterFailures = useCallback(() => {
    resumeAfterFailuresMutation.mutate(undefined, {
      onSuccess: (response) => {
        if (response.error) {
          toast({
            title: 'Could not resume sync',
            description: response.error.message,
            variant: 'destructive',
          });
          return;
        }
        toast({
          title: 'Scheduled sync resumed',
          description: 'The next scheduled tick will run normally.',
        });
      },
      onError: () =>
        toast({
          title: 'Could not resume sync',
          description: 'Please try again.',
          variant: 'destructive',
        }),
    });
  }, [resumeAfterFailuresMutation, toast]);

  // SCH-4.2: trigger an immediate tenant-wide sync via POST /sync-now.
  // Backend handles overlap (returns the existing sync_run_id), auth_failed
  // (admin must rotate first), and integration_disabled. Toast surfaces
  // each case with a human-readable message.
  const handleSyncNow = useCallback(() => {
    syncNowMutation.mutate(undefined, {
      onSuccess: (response) => {
        if (response.error) {
          const code = response.error.code;
          const description =
            code === 'backfill_already_running'
              ? 'A sync is already in progress — watch it from the Sync History tab.'
              : code === 'auth_failed'
                ? 'Authentication failed — rotate your API key on the Configure tab first.'
                : code === 'integration_disabled'
                  ? 'Enable the integration before syncing.'
                  : code === 'forbidden'
                    ? 'Administrator access is required to trigger a manual sync.'
                    : (response.error.message || 'Please try again.');
          toast({
            title: 'Could not start sync',
            description,
            variant: 'destructive',
          });
          return;
        }
        toast({
          title: 'Sync started',
          description: 'Watch progress on the Sync History tab.',
        });
      },
      onError: () => {
        toast({
          title: 'Could not start sync',
          description: 'Please try again.',
          variant: 'destructive',
        });
      },
    });
  }, [syncNowMutation, toast]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Failed to load integrations</AlertTitle>
        <AlertDescription>
          Please refresh the page. If the issue persists, contact support.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {integration ? (
        <SyncStatusCard
          integration={integration}
          // ⚠️ FIXED 2026-08-13. This opened setIsPanelOpen, which is
          // ConnectSubContractorHubPanel — the FIRST-TIME connect flow, the same
          // panel the "Connect SubContractorHub" CTA uses when no integration
          // exists. In the auth_failed state a row DOES exist, so /connect's
          // composite-key guard refused with "An integration of this type already
          // exists for this tenant" immediately after the connection test
          // succeeded. The only escape offered was Delete, which discards every
          // field, stage, adder and document mapping.
          //
          // onReconnect is bound to the "Re-test connection" button and is used by
          // no other status (SyncStatusCard.tsx:326 is its only call site), so
          // repointing it is contained. RotateKeyPanel is the correct flow: it
          // replaces the key IN PLACE via /rotate-key, keeping the same row and
          // the same Vault secret_id, so all mappings survive.
          onReconnect={() => setIsRotateOpen(true)}
          onConfigure={() => setIsConfigureOpen(true)}
          onDisable={() => setIsDisableConfirmOpen(true)}
          onEnable={handleEnable}
          onDelete={() => setIsDeleteDialogOpen(true)}
          onReconnectDifferentAccount={() => setIsReconnectOpen(true)}
          onSyncNow={canSyncNow ? handleSyncNow : undefined}
          enablePending={enableMutation.isPending}
          syncNowPending={syncNowMutation.isPending}
          onResumeAfterDrop={() => setIsResumeConfirmOpen(true)}
          resumeAfterDropPending={resumeAfterDropMutation.isPending}
          onResumeAfterFailures={handleResumeAfterFailures}
          resumeAfterFailuresPending={resumeAfterFailuresMutation.isPending}
        />
      ) : (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-3">
              <Plug className="h-5 w-5 text-primary" />
              <CardTitle>SubContractorHub</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Sync projects, custom fields, and documents from your SubContractorHub account.
              </p>
              <Button onClick={() => setIsPanelOpen(true)}>Connect SubContractorHub</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <ConnectSubContractorHubPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
      />
      {integration && (
        <SubContractorHubPanel
          isOpen={isConfigureOpen}
          onClose={() => setIsConfigureOpen(false)}
          integration={integration}
          onRequestRotateKey={() => {
            setIsConfigureOpen(false);
            setIsRotateOpen(true);
          }}
          onRequestReconnect={() => {
            setIsConfigureOpen(false);
            setIsReconnectOpen(true);
          }}
        />
      )}
      {integration && (
        <RotateKeyPanel
          isOpen={isRotateOpen}
          onClose={() => setIsRotateOpen(false)}
          integration={integration}
        />
      )}

      {integration && (
        <ReconnectPanel
          isOpen={isReconnectOpen}
          onClose={() => setIsReconnectOpen(false)}
          integration={integration}
        />
      )}

      <DeleteIntegrationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      />

      <AlertDialog
        open={isDisableConfirmOpen}
        onOpenChange={(open) => {
          // Block backdrop / escape close while the mutation is in flight
          // so the user can't accidentally dismiss the in-progress action.
          if (!disableMutation.isPending) setIsDisableConfirmOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable integration?</AlertDialogTitle>
            <AlertDialogDescription>
              Polling will stop. Your mappings and imported projects are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disableMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Prevent Radix's default close-on-action so we can keep
                // the dialog open until the mutation resolves.
                e.preventDefault();
                handleConfirmDisable();
              }}
              disabled={disableMutation.isPending}
            >
              {disableMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Disable
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* SCH-5.5: typed-confirm Resume dialog. Admin must type "Resume"
          exactly to enable the confirm button — friction-loaded so the
          drop-pause isn't bypassed accidentally. */}
      <AlertDialog
        open={isResumeConfirmOpen}
        onOpenChange={(open) => {
          if (resumeAfterDropMutation.isPending) return;
          setIsResumeConfirmOpen(open);
          if (!open) setResumeConfirmText('');
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resume SubContractorHub sync?</AlertDialogTitle>
            <AlertDialogDescription>
              The integration was paused because SubContractorHub returned far
              fewer projects than the previous successful sync. Only resume after
              confirming this is not a SubContractorHub-side data loss event.
              Resuming will re-baseline the project count to today's value so the
              comparison won't immediately re-trip.
              <br />
              <br />
              Type <strong>Resume</strong> to confirm:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            autoFocus
            value={resumeConfirmText}
            onChange={(e) => setResumeConfirmText(e.target.value)}
            placeholder="Type Resume"
            disabled={resumeAfterDropMutation.isPending}
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resumeAfterDropMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleResumeConfirm();
              }}
              disabled={
                resumeConfirmText.trim() !== 'Resume' ||
                resumeAfterDropMutation.isPending
              }
            >
              {resumeAfterDropMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Resume sync
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
